import { ICountry } from '@/interfaces/general/ICountry.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { type RouteLocationNormalizedLoaded } from 'vue-router';

type RouteWithQuery = Pick<RouteLocationNormalizedLoaded, 'query'>;

export function countryIdMatches (countryId: string, idToMatch: string): boolean {
    return countryId.toLowerCase() === idToMatch.toLowerCase();
}

export function getPlantCountries (countries: Array<ICountry>): Array<ICountry> {
    return countries.filter(country => country.hasPlants);
}

export function getPlantCountryIds (countries: Array<ICountry>): Array<string> {
    return getPlantCountries(countries).flatMap(country => (country.mainSubCountryId ? [country.id, country.mainSubCountryId] : [country.id]));
}

export function countryHasAccess (
    country: ICountry,
    accessByCountryId: Record<string, boolean>,
): boolean {
    return Object.entries(accessByCountryId).some(([countryId, hasAccess]) => (
        hasAccess && !!findCountryByCountryId([country], countryId)
    ));
}

export function findCountryByCountryId (
    countries: Array<ICountry>,
    countryId?: string,
): ICountry | undefined {
    if (!countryId) return undefined;

    return countries.find(country => countryIdMatches(country.id, countryId)
        || (!!country.mainSubCountryId && countryIdMatches(country.mainSubCountryId, countryId))
        || country.subCountries.some(subCountry => countryIdMatches(subCountry.id, countryId)));
}

export function getParentCountryId (
    countries: Array<ICountry>,
    countryId?: string,
): string | undefined {
    if (!countryId) return undefined;

    return findCountryByCountryId(countries, countryId)?.id ?? countryId;
}

export function getAvailableCountryIdForParentCountry (
    countries: Array<ICountry>,
    parentCountryId: string | undefined,
    availableCountryIds: Array<string>,
): string | undefined {
    if (!parentCountryId) return undefined;

    return availableCountryIds.find(countryId => countryIdMatches(countryId, parentCountryId))
        ?? availableCountryIds.find(countryId => countryIdMatches(getParentCountryId(countries, countryId) ?? '', parentCountryId));
}

export function getCountryQueryId (route: RouteWithQuery): string | undefined {
    return typeof route.query.country === 'string' ? route.query.country : undefined;
}

export function getCountrySelectionValue (country: ICountry): IIdName | undefined {
    if (!country.mainSubCountryId) return country;
    return country.subCountries.find(subCountry => subCountry.id === country.mainSubCountryId);
}

export function resolveSubCountrySelection (
    countries: Array<ICountry>,
    selected: IIdName,
): IIdName {
    const country = findCountryByCountryId(countries, selected.id);
    if (!country) return selected;

    if (!countryIdMatches(country.id, selected.id)) return selected;

    if (country.subCountries.length > 0) {
        if (country.mainSubCountryId) {
            return getCountrySelectionValue(country) ?? country.subCountries[0];
        }
        return country.subCountries[0];
    }

    return selected;
}

export function resolveRemitCountrySelection (
    countries: Array<ICountry>,
    selected: IIdName,
): IIdName {
    const country = findCountryByCountryId(countries, selected.id);
    if (country && (country.id === 'Gb' || country.id === 'France')) return selected;

    return resolveSubCountrySelection(countries, selected);
}
